import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { getOrgConfig, updateOrgConfig } from '../../api/org-config'
import { Button } from '../../components/primitives/Button'
import { Field } from '../../components/primitives/Field'

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
}

/** Edit per-org holiday region + observance behaviour. The holiday calendar
 *  itself is managed at /hr/holidays (linked). observanceRulesEnabled OFF =
 *  no race/religion-based festival observance (a generic, non-MY client). */
export function AdminRegionTab(): JSX.Element {
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: ['org-config', 'full'], queryFn: getOrgConfig })

  const [region, setRegion] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) setRegion(data.localeRegion)
  }, [data])

  const mutation = useMutation({
    mutationFn: () => updateOrgConfig({ localeRegion: region }),
    onSuccess: () => {
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: ['org-config'] })
    },
  })

  if (!data) return <p style={{ color: 'var(--text-2)' }}>Loading…</p>

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 520 }}>
      <Field label="Default holiday region (ISO-style code, e.g. MY-FED)" required>
        <input
          type="text"
          aria-label="Default holiday region"
          value={region}
          onChange={(e) => {
            setRegion(e.target.value)
            setSaved(false)
          }}
          style={inputStyle}
        />
      </Field>
      <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
        New holidays default to this region. Each holiday is still set per-region in the calendar.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          variant="primary"
          disabled={mutation.isPending || !region}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save region settings'}
        </Button>
        {saved ? <span style={{ color: 'var(--success-fg)', fontSize: 13 }}>Saved</span> : null}
        {mutation.isError ? (
          <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>Save failed</span>
        ) : null}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0 }}>
        Manage the holiday calendar in the “More config” tab → Holidays.
      </p>
    </div>
  )
}
